import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { companiesApi } from '../lib/api';
import { toast } from 'sonner';
import { Save, Upload } from 'lucide-react';

export default function CompanySettings() {
  const { company, companyId } = useOutletContext();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name: '',
    slug: '',
    description: '',
    brand_color: '#0d9488',
    timezone: 'Europe/Istanbul',
    notes: '',
  });

  useEffect(() => {
    if (company) {
      setForm({
        name: company.name || '',
        slug: company.slug || '',
        description: company.description || '',
        brand_color: company.brand_color || '#0d9488',
        timezone: company.timezone || 'Europe/Istanbul',
        notes: company.notes || '',
      });
    }
  }, [company]);

  const updateMutation = useMutation({
    mutationFn: (data) => companiesApi.update(companyId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', companyId] });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      toast.success('Settings saved');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to save'),
  });

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await companiesApi.uploadLogo(companyId, file);
      queryClient.invalidateQueries({ queryKey: ['company', companyId] });
      toast.success('Logo uploaded');
    } catch {
      toast.error('Failed to upload logo');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    updateMutation.mutate(form);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-2xl">
      <h2 className="text-xl font-semibold font-display mb-6">Company Settings</h2>

      {/* Logo */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-display">Logo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div
              className="w-16 h-16 rounded-xl flex items-center justify-center text-white text-2xl font-bold overflow-hidden"
              style={{ backgroundColor: company?.brand_color || '#0d9488' }}
            >
              {company?.logo_url ? (
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${company.logo_url}`}
                  alt="Logo"
                  className="w-full h-full object-cover"
                />
              ) : (
                company?.name?.charAt(0)
              )}
            </div>
            <div>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
                id="logo-upload"
              />
              <Button variant="secondary" size="sm" onClick={() => document.getElementById('logo-upload')?.click()}>
                <Upload className="w-4 h-4 mr-2" /> Upload Logo
              </Button>
              <p className="text-xs text-muted-foreground mt-1">PNG, JPG up to 2MB</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Settings Form */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Slug</Label>
              <Input
                value={form.slug}
                onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Brand Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={form.brand_color}
                    onChange={(e) => setForm((p) => ({ ...p, brand_color: e.target.value }))}
                    className="w-10 h-10 rounded border cursor-pointer"
                  />
                  <Input
                    value={form.brand_color}
                    onChange={(e) => setForm((p) => ({ ...p, brand_color: e.target.value }))}
                    className="flex-1"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Timezone</Label>
                <Input
                  value={form.timezone}
                  onChange={(e) => setForm((p) => ({ ...p, timezone: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                rows={2}
              />
            </div>
            <Button type="submit" disabled={updateMutation.isPending}>
              <Save className="w-4 h-4 mr-2" />
              {updateMutation.isPending ? 'Saving...' : 'Save Settings'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
